import { useMemo, useState } from 'react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import Button from '@mui/material/Button'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import Stack from '@mui/material/Stack'
import TableHeader from '@/components/TableHeader'
import { useGetBuildingsQuery } from '@/api'
import { useGetChannelsListQuery } from '@/api/channels'
import { channelsDataHead } from '@/utils/stub'
import DialogForm from './dialog-form'
import SearchInput from '@/components/SearchInput'
import TableCellWithCopy from '@/components/TableCellWithCopy'

const Channels = () => {
  const { t } = useTranslation()
  const { data, isSuccess, isLoading, isError } = useGetChannelsListQuery()
  const { data: buildings } = useGetBuildingsQuery()
  const [open, setOpen] = useState(false)
  const [orderBy, setOrderBy] = useState('number')
  const [order, setOrder] = useState('asc')
  const [selected, setSelected] = useState([])
  const [currentId, setCurrentId] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')

  const channels = useMemo(() => (search ? data?.filter((channel) => 
    String(channel.name) 
    && String(channel.name).toLowerCase().search(search && search.toLowerCase()) >= 0 
    || channel?.id === search
  ) : data), 
  [data, search]);

  const getNamePartner = (id) => {
    return buildings?.find((building) => building.id === id)?.name
  }

  const onOpenDialog = () => {
    setOpen(true)
  }

  const onCloseDialog = () => {
    setOpen(false)
    setCurrentId(null)
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const stableSort = (array, comparator) => {
    const stabilizedThis = array?.map((el, index) => [el, index])
    stabilizedThis?.sort((a, b) => {
      const orderTable = comparator(a[0], b[0])
      if (orderTable !== 0) {
        return orderTable
      }
      return a[1] - b[1]
    })
    return stabilizedThis?.map((el) => el[0])
  }

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }

  const handleEdit = (id) => () => {
    setCurrentId(id)
    onOpenDialog()
  }

  return (
    <Stack
      direction="column"
      spacing={2}
    >
      <Stack 
        direction="row" 
        spacing={2}
      >
        <SearchInput 
          placeholder={t('enter_id_or_name')}
          onChange={(value) => setSearch(value)}
          delay={300}
        />
        <Button 
          color="primary" 
          startIcon={<AddIcon />}
          variant="outlined" 
          onClick={onOpenDialog}
        >
          {t('add')}
        </Button>
      </Stack>
      {!!channels?.length && (
        <>
          <TableContainer>
            <Table>
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={channelsDataHead}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(channels, getComparator(order, orderBy))
                  ?.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  ?.map((channel, idx) => {
                    return (
                      <TableRow
                        hover
                        key={channel?.id}
                        tabIndex={-1}
                        role="checkbox"
                        onClick={(event) => handleClick(event, channel.id)}
                      >
                        <TableCellWithCopy copyText={channel?.id}>
                          <Typography 
                            variant="subtitle2" 
                            sx={{
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                              width: "70px",
                              display: "block",
                              overflow: "hidden",
                              cursor: "default",
                            }}
                            title={channel?.id}
                            >
                            {channel?.id}
                          </Typography>
                        </TableCellWithCopy>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>
                            {channel.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            color={channel.is_active ? 'green' : 'red'}
                          >
                            {
                              channel.is_active
                                ? t('actively')
                                : t('inactively')
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" noWrap>
                            {channel.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {getNamePartner(channel.building_id)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography>
                            {format(
                              new Date(channel.created_at),
                              'dd.MM.yyyy'
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('edit')}>
                            <IconButton
                              sx={{ mr: 2 }}
                              id="menu"
                              size="small"
                              aria-label="edit"
                              onClick={handleEdit(channel.id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[ 10, 25 ]}
            component="div"
            count={channels?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
      {
        channels?.length === 0 
          ? search?.length > 0 
            ? (<Alert severity="info">{t('no_results_found')}</Alert>) 
            : (<Alert severity="info">{t('list_empty')}</Alert>)
          : null
      }
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {isSuccess && !data?.length && (
        <Alert variant="filled" severity="warning">
          {t('list_empty')}
        </Alert>
      )}
      <DialogForm open={open} onClose={onCloseDialog} currentId={currentId} />
    </Stack>
  )
}

export default Channels
